import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  TextField,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { MVPFiltersProps } from '../../advanta-mvp-charts/constants';
import { CustomTab } from '../../../components/customTab';
import { MVPDataTableInternal } from '../components/table';
import ImportUsersDrawer from '../components/importUserDrawer';
import ScheduleDrawer, {
  TScheduleForm,
  TScheduleLine,
} from '../components/scheduleDrawer';
import ProductDrawer, {
  TProductForm,
} from '../components/productDrawer';
import ImportScheduleDrawer from '../components/importScheduleDrawer';
import {
  useAddOrUpdateInputProductMasterMutation,
  useGetAgriInputProductsQuery,
  useAddOrUpdateSchedulesMutation,
  useGetAgriInputSchedulesQuery,
} from '../../../services/mVPGraphQlService';
import { showAlert } from '../../../services/alert';
import { formatProductLabel } from '../hooks/useProductOptions';
import { stageLabel } from '../utils/scheduleStage';
import { RootState } from '../../../store';
import { TRequestAddOrUpdateSchedules } from '../../../types/mvpCharts';
import { ReactComponent as NoDataFoundIcon } from '../../../assets/icons/noDataFound.svg';
import dayjs from 'dayjs';
import '../index.scss';

type TAgriTab = {
  value: string;
  label: string;
  tabIndex: string;
};


type TInputProduct = {
  id: number;
  productName?: string;
  country?: string;
  region?: string;
  crop?: string;
  hybridProdCode?: string;
  technicalIngredient?: string;
  dosageValue?: string;
  dosageBasis?: string;
  uom?: string;
  brand?: string;
  company?: string;
  packageSize?: string;
  advantaMaterialCode?: string;
  uplMaterialCode?: string;
  uplProduct?: boolean;
  active?: boolean;
};

const toText = (value?: number | string | null) =>
  value === undefined || value === null
    ? undefined
    : String(value);

const toYesNo = (value?: boolean | null) =>
  value === undefined || value === null
    ? ''
    : value
    ? 'YES'
    : 'NO';

type TScheduleStatus = 'Published' | 'Pending' | 'Inactive';


type TScheduleRow = {
  id: number;
  crop?: string;
  hybridProdCode?: string;
  status?: TScheduleStatus;
  season?: string;
  region?: string;
  country?: string;
  version?: string;
  remarks?: string;
  hasPendingEdit?: boolean;
  publishedLines?: TScheduleLine[];
  lines: TScheduleLine[];
};

const SCHEDULE_STATUS_LABEL: Record<string, TScheduleStatus> = {
  AIS_PUBLISHED: 'Published',
  AIS_PENDING: 'Pending',
  AIS_INACTIVE: 'Inactive',
};


const STAGE_ENUMS = [
  'AGS_LAND_PREPARATION',
  'AGS_VEGETATIVE',
  'AGS_FLOWERING',
  'AGS_PRE_HARVEST',
  'AGS_HARVEST',
] as const;
type TStageEnum = typeof STAGE_ENUMS[number];


const toStageEnum = (stage: string): TStageEnum => {
  const key = `AGS_${stage
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')}` as TStageEnum;
  if (!STAGE_ENUMS.includes(key)) {
    throw validationError(`Unknown stage "${stage}"`);
  }
  return key;
};


const validationError = (message: string) =>
  Object.assign(new Error(message), { validation: true });

const parseDas = (value: string | undefined, label: string) => {
  const text = (value ?? '').trim();
  if (!text) return undefined;
  if (!/^-?\d+$/.test(text)) {
    throw validationError(`${label} must be a whole number`);
  }
  return Number(text);
};

const toScheduleItem = (line: TScheduleLine, deleted = false) => {
  const productId = line.product ? Number(line.product) : undefined;
  if (productId !== undefined && !Number.isFinite(productId)) {
    throw validationError('Select a valid product');
  }
  return {
    ...(line.itemId ? { itemId: line.itemId } : {}),
    ...(line.stage ? { stage: toStageEnum(line.stage) } : {}),
    ...(productId !== undefined ? { productId } : {}),
    dasFrom: parseDas(line.dasFrom, 'DAS From'),
    dasTo: parseDas(line.dasTo, 'DAS To'),
    mandatory: (line.mandatory ?? '').toUpperCase() === 'YES',
    ...(deleted ? { deleted: true } : {}),
  };
};


type TScheduleSubmit = {
  region?: string;
  season?: string;
  crop?: string;
  hybridProductionCode?: string;
  remarks?: string;
  lines?: TScheduleLine[];
};

const SCHEDULE_STATUS_CONFIG: Record<
  TScheduleStatus,
  { color: string; bg: string; border: string }
> = {
  Published: {
    color: '#027A48',
    bg: '#ECFDF3',
    border: '#12B76A',
  },
  Pending: {
    color: '#B54708',
    bg: '#FFFAEB',
    border: '#F79009',
  },
  Inactive: {
    color: 'rgba(149,149,149,1)',
    bg: 'rgba(206,206,206,0.3)',
    border: 'rgba(206,206,206,1)',
  },
};

const TEAL = 'rgba(0,105,112,1)';

export const AgriInput: React.FC<MVPFiltersProps> = ({
  onNavigate,
  onBack,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hoveredRow, setHoveredRow] = useState<
    number | null
  >(null);
  const [openImportDrawer, setOpenImportDrawer] =
    useState(false);
  const [openProductDrawer, setOpenProductDrawer] =
    useState(false);
  const [productDrawerMode, setProductDrawerMode] =
    useState<'add' | 'edit'>('add');
  const [productInitialData, setProductInitialData] =
    useState<Partial<TProductForm> | null>(null);
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [schedulePageNo, setSchedulePageNo] = useState(0);
  const [schedulePageSize, setSchedulePageSize] =
    useState(10);
  const [hoveredScheduleRow, setHoveredScheduleRow] =
    useState<number | null>(null);
  const [openScheduleDrawer, setOpenScheduleDrawer] =
    useState(false);
  const [scheduleDrawerMode, setScheduleDrawerMode] =
    useState<'add' | 'duplicate' | 'edit'>('add');
  const [scheduleInitialData, setScheduleInitialData] =
    useState<Partial<TScheduleForm> | null>(null);
  const [
    openScheduleImportDrawer,
    setOpenScheduleImportDrawer,
  ] = useState(false);

  const dispatch = useDispatch();
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const countryName = useSelector(
    (state: RootState) => state.country.countryName
  );
  const [editingProduct, setEditingProduct] =
    useState<TInputProduct | null>(null);
  const [saveProduct, { isLoading: savingProduct }] =
    useAddOrUpdateInputProductMasterMutation();
  const [editingSchedule, setEditingSchedule] =
    useState<TScheduleRow | null>(null);
  const [saveSchedules, { isLoading: savingSchedule }] =
    useAddOrUpdateSchedulesMutation();

  
  const {
    data: productsData,
    isFetching: productsFetching,
    isError: productsError,
    refetch: refetchProducts,
  } = useGetAgriInputProductsQuery({
    wsId,
    pageNumber: pageNo,
    pageSize,
  });
  const productsResult =
    productsData?.data?.GetAgriInputProducts;
  const totalProducts = productsResult?.totalCount ?? 0;

  const products: TInputProduct[] = useMemo(
    () =>
      (productsResult?.products ?? []).map((p) => ({
        id: p.productId ?? 0,
        productName: p.productName,
        country: p.country,
        region: p.region,
        crop: p.crop,
        hybridProdCode: p.hybridProductionCode,
        technicalIngredient: p.technicalIngredient,
        dosageValue: toText(p.dosageValue),
        dosageBasis: p.dosageBasis,
        uom: p.unitOfMeasure,
        brand: p.brand,
        company: p.company,
        packageSize: toText(p.packageSize),
        advantaMaterialCode: p.advantaMaterialCode,
        uplMaterialCode: p.uplMaterialCode,
        uplProduct: p.uplProduct,
        active: p.active,
      })),
    [productsResult]
  );


  useEffect(() => {
    setPageNo(0);
  }, [wsId]);

  useEffect(() => {
    const status = productsResult?.responseStatus;
    if (
      productsError ||
      (status && status.status !== 'SUCCESS')
    ) {
      dispatch(
        showAlert({
          content:
            status?.errorMessages?.[0] ||
            'Unable to load input products',
          severity: 'error',
        })
      );
    }
  }, [productsError, productsResult, dispatch]);


  const {
    data: schedulesData,
    isFetching: schedulesFetching,
    isError: schedulesError,
    refetch: refetchSchedules,
  } = useGetAgriInputSchedulesQuery({
    wsId,
    pageNumber: schedulePageNo,
    pageSize: schedulePageSize,
  });
  const schedulesResult =
    schedulesData?.data?.GetAgriInputSchedules;
  const totalSchedules = schedulesResult?.totalCount ?? 0;

  const schedules: TScheduleRow[] = useMemo(
    () =>
      (schedulesResult?.schedules ?? []).map((s) => {
        const header = s.header;
        type TItem = NonNullable<typeof s.items>[number];
        const toLine = (item: TItem, draft: boolean) => ({
          itemId: item.itemId,
          ...(draft ? { draft: true } : {}),
          product:
            toText(item.productId ?? item.product?.productId) ?? '',
          productLabel: formatProductLabel(item.product),
          uplProduct: item.product?.uplProduct === true,
          stage: stageLabel(item.stage) || item.stageName || '',
          dasFrom: toText(item.dasFrom) ?? '',
          dasTo: toText(item.dasTo) ?? '',
          mandatory: item.mandatory ? 'YES' : 'NO',
        });
        const draftLines = (s.editedScheduleDraft?.items ?? [])
          .filter((item) => !item.deleted)
          .map((item) => toLine(item as TItem, true));
        const baseLines = (s.items ?? [])
          .filter((item) => !item.deleted)
          .map((item) => toLine(item, false));
        return {
          hasPendingEdit: draftLines.length > 0,
          id: header?.scheduleId ?? 0,
          crop: header?.cropName ?? undefined,
          hybridProdCode: header?.hybridProductionCode ?? undefined,
          status: SCHEDULE_STATUS_LABEL[header?.status ?? ''],
          season: header?.season ?? undefined,
          region: header?.region ?? undefined,
          country: countryName,
          version: toText(header?.version),
          effectiveFrom: header?.effectiveFrom?.seconds
          ? dayjs(header.effectiveFrom.seconds * 1000).format('DD/MM/YYYY')
          : '-',
          remarks: header?.remarks ?? undefined,
          publishedLines: baseLines,
          lines: draftLines.length > 0 ? draftLines : baseLines,
        };
      }),
    [schedulesResult, countryName]
  );

  useEffect(() => {
    setSchedulePageNo(0);
  }, [wsId]);

  useEffect(() => {
    const status = schedulesResult?.responseStatus;
    if (
      schedulesError ||
      (status && status.status !== 'SUCCESS')
    ) {
      dispatch(
        showAlert({
          content:
            status?.errorMessages?.[0] ||
            'Unable to load input schedules',
          severity: 'error',
        })
      );
    }
  }, [schedulesError, schedulesResult, dispatch]);

  const openAddProduct = () => {
    setProductDrawerMode('add');
    setEditingProduct(null);
    setProductInitialData(null);
    setOpenProductDrawer(true);
  };

  const handleSaveProduct = async (
    data: TProductForm
  ): Promise<boolean> => {
    const isUpdate =
      productDrawerMode === 'edit' && !!editingProduct;
    const optional = (value?: string) =>
      value && value.trim() ? value.trim() : undefined;

    try {
      const response = await saveProduct({
        wsId,
        dataType: 'DT_ROW',
        products: [
          {
            ...(isUpdate
              ? { productId: editingProduct?.id }
              : {}),
            wsId,
            country: editingProduct?.country ?? countryName,
            region: data.region,
            crop: data.crop,
            hybridProductionCode: optional(
              data.hybridProductionCode
            ),
            productName: data.productName,
            technicalIngredient: data.technicalIngredient,
            brand: data.brand,
            company: data.company,
            packageSize: Number(data.packageSize),
            unitOfMeasure: data.unitOfMeasure,
            dosageValue: Number(data.dosageValue),
            dosageBasis: data.dosageBasis,
            advantaMaterialCode: data.advantaMaterialCode,
            uplMaterialCode: optional(data.uplMaterialCode),
            uplProduct: data.uplProduct?.toUpperCase() === 'YES',
            active: data.active?.toUpperCase() === 'YES',
          },
        ],
      }).unwrap();

      const result =
        response?.data?.AddOrUpdateInputProductMaster;
      const status = result?.responseStatus;
      const failedRow = result?.results?.find((r) =>
        r.outcome?.startsWith('AIO_FAILED')
      );

      if (status?.status === 'SUCCESS' && !failedRow) {
        dispatch(
          showAlert({
            content: isUpdate
              ? 'Product updated successfully'
              : 'Product added successfully',
          })
        );
        refetchProducts();
        return true;
      }

      dispatch(
        showAlert({
          content:
            failedRow?.message ||
            status?.errorMessages?.[0] ||
            status?.reasons?.[0] ||
            'Unable to save the product',
          severity: 'error',
        })
      );
      return false;
    } catch (err) {
      console.error('Save product failed', err);
      dispatch(
        showAlert({
          content: 'Something went wrong',
          severity: 'error',
        })
      );
      return false;
    }
  };

  const openEditProduct = (row: TInputProduct) => {
    setProductDrawerMode('edit');
    setEditingProduct(row);
    setProductInitialData({
      productName: row.productName,
      country: (row.country ?? '').toUpperCase(),
      region: row.region,
      crop: row.crop,
      hybridProductionCode: row.hybridProdCode,
      technicalIngredient: row.technicalIngredient,
      brand: row.brand,
      company: row.company,
      packageSize: row.packageSize,
      unitOfMeasure: row.uom,
      dosageValue: row.dosageValue,
      dosageBasis: row.dosageBasis,
      advantaMaterialCode: row.advantaMaterialCode,
      uplMaterialCode: row.uplMaterialCode,
      uplProduct: toYesNo(row.uplProduct),
      active: toYesNo(row.active),
    });
    setOpenProductDrawer(true);
  };

  const scheduleDataFromRow = (
    row: TScheduleRow
  ): Partial<TScheduleForm> => ({
    country: (row.country ?? '').toUpperCase(),
    region: row.region,
    season: row.season,
    crop: row.crop,
    hybridProductionCode: row.hybridProdCode,
    remarks: row.remarks,
    status: row.status,
    hasPendingEdit: row.hasPendingEdit,
    lines: row.lines,
  });

  const openDuplicateSchedule = (row: TScheduleRow) => {
    if (row.status === 'Pending') return;
    setScheduleDrawerMode('duplicate');
    setEditingSchedule(null);
    setScheduleInitialData(scheduleDataFromRow(row));
    setOpenScheduleDrawer(true);
  };

  const openEditSchedule = (row: TScheduleRow) => {
    if (row.status === 'Inactive') return;
    setScheduleDrawerMode('edit');
    setEditingSchedule(row);
    setScheduleInitialData(scheduleDataFromRow(row));
    setOpenScheduleDrawer(true);
  };


  const submitSchedule = async (
    data: TScheduleSubmit,
    action: 'save' | 'publish',
    existing: TScheduleRow | null
  ): Promise<boolean> => {
    try {
      const current = data.lines ?? [];
      const lineKey = (l: TScheduleLine) =>
        `${l.draft ? 'draft' : 'base'}-${l.itemId}`;
      const keptKeys = new Set(
        current.filter((l) => l.itemId).map(lineKey)
      );
      const removed = (existing?.lines ?? []).filter(
        (l) => l.itemId && !keptKeys.has(lineKey(l))
      );
      const draftMode =
        !!existing &&
        (existing.status === 'Published' ||
          !!existing.hasPendingEdit);
      const items = draftMode
        ? (existing?.publishedLines ?? []).map((l) =>
            toScheduleItem(l)
          )
        : [
            ...current.map((l) => toScheduleItem(l)),
            ...removed.map((l) => toScheduleItem(l, true)),
          ];
      const draftItems = draftMode
        ? [
            ...current.map((l) =>
              toScheduleItem(
                l.draft ? l : { ...l, itemId: undefined }
              )
            ),
            ...removed
              .filter((l) => l.draft)
              .map((l) => toScheduleItem(l, true)),
          ]
        : [];
      const version = Number(existing?.version);
      const header = {
        ...(existing ? { scheduleId: existing.id } : {}),
        wsId,
        season: data.season,
        region: data.region,
        cropName: data.crop,
        hybridProductionCode: data.hybridProductionCode,
        remarks: data.remarks?.trim() || '',
        ...(existing && Number.isFinite(version)
          ? { version }
          : {}),
      };

      return await sendSchedule(
        {
          header,
          items,
          ...(draftItems.length > 0
            ? {
                editedScheduleDraft: {
                  header,
                  items: draftItems,
                },
              }
            : {}),
        },
        action,
        action === 'publish'
          ? 'Schedule published successfully'
          : 'Schedule saved successfully',
        'Unable to save the schedule'
      );
    } catch (err) {
      return handleScheduleError(err);
    }
  };

  type TScheduleEntry =
    TRequestAddOrUpdateSchedules['schedules'][number];

  const handleScheduleError = (err: unknown) => {
    console.error('Schedule request failed', err);
    dispatch(
      showAlert({
        content: (err as { validation?: boolean })?.validation
          ? (err as Error).message
          : 'Something went wrong',
        severity: 'error',
      })
    );
    return false;
  };

  const sendSchedule = async (
    schedule: TScheduleEntry,
    action: 'save' | 'publish',
    successMessage: string,
    failureMessage: string
  ): Promise<boolean> => {
    const response = await saveSchedules({
      wsId,
      deviceId: 'ADVANTA_CLIENT_WEB',
      save: action === 'save',
      publish: action === 'publish',
      schedules: [schedule],
    }).unwrap();

    const result = response?.data?.AddOrUpdateSchedules;
    const status = result?.responseStatus;
    const failed = result?.results?.find(
      (r) => r.success === false
    );

    if (status?.status === 'SUCCESS' && !failed) {
      dispatch(showAlert({ content: successMessage }));
      refetchSchedules();
      return true;
    }

    dispatch(
      showAlert({
        content:
          failed?.message ||
          status?.errorMessages?.[0] ||
          status?.reasons?.[0] ||
          failureMessage,
        severity: 'error',
      })
    );
    return false;
  };

  const deleteSchedule = async (
    existing: TScheduleRow | null
  ): Promise<boolean> => {
    if (!existing) return false;
    try {
      const version = Number(existing.version);
      const header = {
        scheduleId: existing.id,
        wsId,
        season: existing.season,
        region: existing.region,
        cropName: existing.crop,
        hybridProductionCode: existing.hybridProdCode,
        remarks: existing.remarks?.trim() || '',
        ...(Number.isFinite(version) ? { version } : {}),
      };
      const baseLines =
        existing.publishedLines ??
        existing.lines.filter((l) => !l.draft);
      const draftLines = existing.lines.filter((l) => l.draft);

      const schedule: TScheduleEntry = existing.hasPendingEdit
        ? {
            header,
            items: baseLines.map((l) => toScheduleItem(l)),
            editedScheduleDraft: {
              header,
              items: draftLines
                .filter((l) => l.itemId)
                .map((l) => toScheduleItem(l, true)),
            },
          }
        : {
            header,
            items: baseLines
              .filter((l) => l.itemId)
              .map((l) => toScheduleItem(l, true)),
          };

      return await sendSchedule(
        schedule,
        'save',
        existing.hasPendingEdit
          ? 'Pending edit deleted successfully'
          : 'Schedule deleted successfully',
        'Unable to delete the schedule'
      );
    } catch (err) {
      return handleScheduleError(err);
    }
  };

  const CHART_LIST: TAgriTab[] = [
    {
      value: 'inputProducts',
      label: `Input products (${totalProducts})`,
      tabIndex: '1',
    },
    {
      value: 'scheduleInput',
      label: `Schedule Input (${totalSchedules})`,
      tabIndex: '2',
    },
  ];

  const [activeTab, setActiveTab] = useState<TAgriTab>(
    CHART_LIST[0]
  );

  const handleTabChange = (tab: TAgriTab) => {
    if (tab) setActiveTab(tab);
  };

  
  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return products;
    return products.filter((product) =>
      (product.productName ?? '').toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  
  const filteredSchedules = useMemo(() => {
    const term = scheduleSearch.trim().toLowerCase();
    if (!term) return schedules;
    return schedules.filter((row) =>
      [row.crop, row.hybridProdCode, row.season, row.region]
        .filter(Boolean)
        .some((value) =>
          (value as string).toLowerCase().includes(term)
        )
    );
  }, [schedules, scheduleSearch]);

  const productNameNode = (row: TInputProduct) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      sx={{ minHeight: 28 }}
      onMouseEnter={() => setHoveredRow(row.id)}
      onMouseLeave={() => setHoveredRow(null)}>
      <Typography fontSize={13} fontWeight={600}>
        {row.productName}
      </Typography>

      {hoveredRow === row.id && (
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => openEditProduct(row)}
            sx={{
              backgroundColor: TEAL,
              color: '#fff',
              borderRadius: '6px',
              p: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0,105,112,0.9)',
              },
            }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  const columns = [
    {
      key: 'productName',
      label: 'Product Name',
      cellModifier: (row: TInputProduct) =>
        productNameNode(row),
    },
    { key: 'country', label: 'Country' },
    { key: 'crop', label: 'Crop' },
    { key: 'hybridProdCode', label: 'Hybrid Prod. Code' },
    {
      key: 'technicalIngredient',
      label: 'Technical Ingredient',
    },
    { key: 'dosageValue', label: 'Dosage Value' },
    { key: 'dosageBasis', label: 'Dosage Basis' },
    { key: 'uom', label: 'UOM' },
    { key: 'brand', label: 'Brand' },
  ];

  const scheduleStatusPill = (
    status: TScheduleRow['status']
  ) => {
    const config = status && SCHEDULE_STATUS_CONFIG[status];
    if (!config) return '-';
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          px: '10px',
          py: '4px',
          borderRadius: '999px',
          border: `1px solid ${config.border}`,
          backgroundColor: config.bg,
          width: 'fit-content',
        }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: config.border,
          }}
        />
        <Typography
          fontSize={13}
          fontWeight={500}
          color={config.color}>
          {status}
        </Typography>
      </Box>
    );
  };

  const scheduleNameNode = (row: TScheduleRow) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      sx={{ minHeight: 28 }}
      onMouseEnter={() => setHoveredScheduleRow(row.id)}
      onMouseLeave={() => setHoveredScheduleRow(null)}>
      <Typography fontSize={13} fontWeight={600}>
        {row.crop ?? '-'}
      </Typography>

      {hoveredScheduleRow === row.id && (
        <Box display="flex" alignItems="center" gap={0.5}>
          {row.status !== 'Pending' && (
            <IconButton
              size="small"
              onClick={() => openDuplicateSchedule(row)}
              sx={{
                backgroundColor: TEAL,
                color: '#fff',
                borderRadius: '6px',
                p: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0,105,112,0.9)',
                },
              }}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          {row.status === 'Inactive' ? null : row.hasPendingEdit ? (
            <Button
              size="small"
              onClick={() => openEditSchedule(row)}
              startIcon={<EditIcon sx={{ fontSize: '12px !important' }} />}
              sx={{
                backgroundColor: TEAL,
                color: '#fff',
                borderRadius: '6px',
                px: 1,
                py: '2px',
                minWidth: 0,
                fontSize: 10,
                fontStyle: 'italic',
                fontWeight: 500,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
                '&:hover': {
                  backgroundColor: 'rgba(0,105,112,0.9)',
                },
              }}>
              Pending Edit
            </Button>
          ) : (
            <IconButton
              size="small"
              onClick={() => openEditSchedule(row)}
              sx={{
                backgroundColor: TEAL,
                color: '#fff',
                borderRadius: '6px',
                p: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0,105,112,0.9)',
                },
              }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );


  const scheduleColumns = [

    { key: 'crop', label: 'Crop', cellModifier: (row: TScheduleRow) =>
      scheduleNameNode(row),
     },

    { key: 'hybridProdCode', label: 'Hybrid Prod. Code' },
    {
      key: 'status',
      label: 'Status',
      cellModifier: (row: TScheduleRow) =>
        scheduleStatusPill(row.status),
    },
    { key: 'season', label: 'Season' },
    { key: 'country', label: 'Country' },
    { key: 'version', label: 'Version' },
    { key: 'effectiveFrom', label: 'Effective From' },
  ];
  const scheduleToolbar = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pt: 1,
        pb: 1.5,
        backgroundColor: '#fff',
      }}>
      <TextField
        placeholder="Search by input schedule"
        size="small"
        value={scheduleSearch}
        onChange={(e) => setScheduleSearch(e.target.value)}
        sx={{ minWidth: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}>
        <Button
          startIcon={<HistoryIcon />}
          onClick={() =>
            onNavigate?.('importHistory', {
              type: 'agriInputSchedule',
            })
          }
          sx={{
            textTransform: 'none',
            color: TEAL,
            fontWeight: 500,
          }}>
          View History
        </Button>

        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => setOpenScheduleImportDrawer(true)}
          sx={{
            textTransform: 'none',
            backgroundColor: TEAL,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(0,105,112,0.9)',
            },
          }}>
          Add Schedule
        </Button>
      </Box>
    </Box>
  );

  const emptyState = (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: 1,
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}>
      <NoDataFoundIcon
        style={{
          width: 140,
          height: 'auto',
          marginBottom: 16,
        }}
      />
      <Typography
        fontSize={15}
        fontWeight={600}
        color="#333">
        No Data Yet
      </Typography>
      <Typography fontSize={13} color="#8a8a8a" mt={0.5}>
        Your data will appear here once available.
      </Typography>
    </Box>
  );

  const toolbar = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pt: 1,     
        pb: 1.5,
        backgroundColor: '#fff',
      }}>
    
      <TextField
        placeholder="Search by product name"
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ minWidth: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}>
        <Button
          startIcon={<HistoryIcon />}
          onClick={() =>
            onNavigate?.('importHistory', {
              type: 'agriInput',
            })
          }
          sx={{
            textTransform: 'none',
            color: TEAL,
            fontWeight: 500,
          }}>
          View History
        </Button>

        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={openAddProduct}
          sx={{
            textTransform: 'none',
            borderColor: TEAL,
            color: TEAL,
            fontWeight: 500,
            '&:hover': {
              borderColor: 'rgba(0,105,112,0.8)',
              backgroundColor: 'rgba(0,105,112,0.05)',
            },
          }}>
          Add Product
        </Button>

        <Button
          startIcon={<DownloadIcon />}
          variant="contained"
          onClick={() => setOpenImportDrawer(true)}
          sx={{
            textTransform: 'none',
            backgroundColor: TEAL,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(0,105,112,0.9)',
            },
          }}>
          Import Products
        </Button>
      </Box>
    </Box>
  );

  return (
    <div>
      <Box
        sx={{
          backgroundColor: '#fff',
          px: 2.5,
          pt: 0.5,
          borderBottom: '1px solid #ececec',
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
          <IconButton
            size="small"
            sx={{ color: '#333' }}
            onClick={onBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight="bold">
            Agri-Input
          </Typography>
        </Box>

        <Box
          sx={{
            '& .mt-6': { mt: 0 },
            '& .MuiTabPanel-root': { p: 0 },
          }}>
          <CustomTab<TAgriTab, keyof TAgriTab>
            tabList={CHART_LIST}
            activeTab={activeTab}
            valueKey={'tabIndex'}
            labelKey={'label'}
            main={false}
            onTabChange={handleTabChange}
            showFilterButton={false}
            tabPanel={(tab) => {
              switch (tab?.tabIndex) {
                case '1':
                  return (
                    <>
                      {toolbar}
                      {productsFetching ? (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="60vh">
                          <CircularProgress />
                        </Box>
                      ) : filteredProducts.length === 0 ? (
                        <Box
                          sx={{
                            p: 2.5,
                            backgroundColor: '#ececec',
                          }}>
                          {emptyState}
                        </Box>
                      ) : (
                        <MVPDataTableInternal
                          {...({
                            type: 'agriInput',
                            columns,
                            data: filteredProducts,
                            pageSize,
                            setPageSize,
                            totalCount: totalProducts,
                            pageNo,
                            setPageNo,
                            isLoading: false,
                            rowsPerPageOptions: [pageSize],
                            rowKey: 'id',
                            stickyColumns: [0],
                            stickyColumnWidths: [230],
                          } as any)}
                        />
                      )}
                    </>
                  );

                case '2':
                  return (
                    <>
                      {scheduleToolbar}
                      {schedulesFetching ? (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="60vh">
                          <CircularProgress />
                        </Box>
                      ) : filteredSchedules.length === 0 ? (
                        <Box
                          sx={{
                            p: 2.5,
                            backgroundColor: '#ececec',
                          }}>
                          {emptyState}
                        </Box>
                      ) : (
                        <MVPDataTableInternal
                          {...({
                            type: 'agriInputSchedule',
                            columns: scheduleColumns,
                            data: filteredSchedules,
                            pageSize: schedulePageSize,
                            setPageSize:
                              setSchedulePageSize,
                            totalCount: totalSchedules,
                            pageNo: schedulePageNo,
                            setPageNo: setSchedulePageNo,
                            isLoading: false,
                            rowsPerPageOptions: [
                              schedulePageSize,
                            ],
                            rowKey: 'id',
                            stickyColumns: [0],
                            stickyColumnWidths: [230],
                          } as any)}
                        />
                      )}
                    </>
                  );
              }
            }}
          />
        </Box>
      </Box>

      <ImportUsersDrawer
        open={openImportDrawer}
        onClose={() => setOpenImportDrawer(false)}
        type={'agriInput'}
        refetch={refetchProducts}
      />

      <ProductDrawer
        open={openProductDrawer}
        onClose={() => setOpenProductDrawer(false)}
        mode={productDrawerMode}
        initialData={productInitialData}
        onSave={handleSaveProduct}
        saving={savingProduct}
      />

      <ImportScheduleDrawer
        open={openScheduleImportDrawer}
        onClose={() => setOpenScheduleImportDrawer(false)}
        onSave={(data) => submitSchedule(data, 'save', null)}
        onPublish={(data) =>
          submitSchedule(data, 'publish', null)
        }
        saving={savingSchedule}
      />

      <ScheduleDrawer
        open={openScheduleDrawer}
        onClose={() => setOpenScheduleDrawer(false)}
        mode={scheduleDrawerMode}
        initialData={scheduleInitialData}
        onSave={(data) =>
          submitSchedule(
            data,
            'save',
            scheduleDrawerMode === 'edit'
              ? editingSchedule
              : null
          )
        }
        onPublish={(data) =>
          submitSchedule(
            data,
            'publish',
            scheduleDrawerMode === 'edit'
              ? editingSchedule
              : null
          )
        }
        onDelete={() =>
          scheduleDrawerMode === 'edit'
            ? deleteSchedule(editingSchedule)
            : undefined
        }
        saving={savingSchedule}
      />
    </div>
  );
};
